import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { TaskListFilters } from "@/types/tasks/TasksListFilters";
import { Mutex } from "async-mutex";
import { readCookie } from "@/utils/cookies/JsonCookieReader";
import { writeCookie } from "@/utils/cookies/JsonCookieWriter";
import { useCurrentUserStore } from "../CurrentUserStore";

export const useTaskListFiltersStore = defineStore("taskListFilters", () => {
  const mutex = new Mutex();

  const cachedFilters = ref<TaskListFilters>();
  const currentUserStore = useCurrentUserStore();

  /** Constructs filter name for currently logged in user and site prefix */
  const getFilterName = async () => {
    const filterName = "x-task-list-filters";
    const currentUser = await currentUserStore.getCurrentUserDetails();
    const sitePrefix = await currentUserStore.getSitePrefix();

    return [filterName, sitePrefix, currentUser.id]
      .filter((item) => !!item)
      .join("-");
  };

  /** Gets the previous Task List filters */
  const getFilters = async () => {
    const filters = await mutex.runExclusive(async () => {
      // To improve performance, only read the cookie we don't already have the data
      if (!cachedFilters.value) {
        cachedFilters.value = readCookie<TaskListFilters>(
          await getFilterName(),
        );
      }

      return cachedFilters.value;
    });

    return filters;
  };

  /** Stores the current Task List filters so filters are remembered
   * when user navigates back to the Task List */
  const updateFilters = async (filters: TaskListFilters) => {
    await mutex.runExclusive(async () => {
      /* Improve performance by caching the updated filters
       * This means the app won't need to read the cookie unnecessarily */
      cachedFilters.value = filters;

      // Update cookie so filters are remembered when user reloads the app
      writeCookie<TaskListFilters>(await getFilterName(), filters);
    });
  };

  /** Clears the current Task List filters cache */
  const clearCache = () => (cachedFilters.value = undefined);

  return { getFilters, updateFilters, clearCache };
});
